import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Filter, X } from "./icons";
import AnimatedSearchInput from "./AnimatedSearchInput";
import { trackInteraction } from "../lib/trackInteraction";
import useGridLastRowBottom from "../hooks/useGridLastRowBottom";
import useMediaQuery from "../hooks/useMediaQuery";
import { filtersToSearchParams, parseFiltersFromSearchParams, shuffleArray } from "./catalog/filterUtils";
import useCatalogFilters from "./catalog/useCatalogFilters";
import CatalogSidebar from "./catalog/CatalogSidebar";
import CatalogToolbar from "./catalog/CatalogToolbar";
import CatalogVideoBanner from "./catalog/CatalogVideoBanner";
import Breadcrumbs from "./Breadcrumbs";
import type { BreadcrumbItem } from "./Breadcrumbs";
import type { Product } from "../types";
import { CATEGORY_LABELS } from "../types";
import { BRAND_DESCRIPTIONS } from "../data/brandDescriptions";
import { buildProductGroups } from "../lib/productGroups";
import ProductGrid from "./ProductGrid";
import ProductGridSkeleton from "./ProductGridSkeleton";
import { createProductSearchIndex, searchProducts } from "../lib/searchProducts";
import { useLanguage } from "../context/LanguageContext";

// Products per page — the grid runs 2/3/4 columns depending on viewport, so
// this isn't a clean row count everywhere, but it lands on 2-4 rows across
// all of them, which is the point.
const PAGE_SIZE = 12;

// Builds the compact page-number list a pager like this shows: always the
// first and last page, the current page and its immediate neighbors, and a
// "…" gap marker wherever numbers were skipped — avoids an unbounded row of
// buttons once the catalog has a lot of pages.
function getPageNumbers(page: number, pageCount: number): (number | "…")[] {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, i) => i + 1);
  }
  const pages = new Set([1, pageCount, page, page - 1, page + 1]);
  const sorted = Array.from(pages)
    .filter((p) => p >= 1 && p <= pageCount)
    .sort((a, b) => a - b);
  const result: (number | "…")[] = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - (sorted[i - 1] as number) > 1) result.push("…");
    result.push(p);
  });
  return result;
}

// Mobile has far less horizontal room than getPageNumbers' up-to-7-plus-
// ellipsis layout needs — 7 number buttons plus prev/next (and first/last,
// once shown) overflowed the viewport (see the report this fixed). Instead
// of trying to cram the same first/last/ellipsis scheme into a narrow
// screen, mobile shows a plain sliding window of exactly 3 numbers centered
// on the current page — first/last are still reachable via the dedicated
// chevron buttons, which show at the same lower threshold this uses.
const MOBILE_MAX_NUMBERS = 3;
function getMobilePageWindow(page: number, pageCount: number): number[] {
  if (pageCount <= MOBILE_MAX_NUMBERS) {
    return Array.from({ length: pageCount }, (_, i) => i + 1);
  }
  const start = Math.max(1, Math.min(page - 1, pageCount - MOBILE_MAX_NUMBERS + 1));
  return Array.from({ length: MOBILE_MAX_NUMBERS }, (_, i) => start + i);
}

function PaginationControls({
  page,
  pageCount,
  onChange,
}: {
  page: number;
  pageCount: number;
  onChange: (page: number) => void;
}) {
  const { t } = useLanguage();
  const isMobile = useMediaQuery("(max-width: 639px)");
  if (pageCount <= 1) return null;

  // Below this threshold the full run of page numbers already fits on
  // screen (see getPageNumbers/getMobilePageWindow), so first/last
  // shortcuts would just be redundant chrome next to buttons that are
  // already visible — mobile's threshold is lower since it shows far
  // fewer numbers at once.
  const showJump = isMobile ? pageCount > MOBILE_MAX_NUMBERS : pageCount > 7;
  const pageNumbers = isMobile ? getMobilePageWindow(page, pageCount) : getPageNumbers(page, pageCount);

  const navButtonClass =
    "flex h-10 w-10 shrink-0 items-center justify-center text-steel-600 transition-colors duration-150 ease-engineered hover:bg-steel-100 hover:text-ink disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-steel-600";

  return (
    <div className="mt-10 flex items-center justify-center gap-2">
      <div className="flex items-center gap-0.5">
        {showJump && (
          <button
            type="button"
            aria-label={t("catalogFirstPageAria")}
            disabled={page <= 1}
            onClick={() => onChange(1)}
            className={navButtonClass}
          >
            <ChevronsLeft size={18} />
          </button>
        )}
        <button
          type="button"
          aria-label={t("catalogPrevPageAria")}
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
          className={navButtonClass}
        >
          <ChevronLeft size={22} />
        </button>
      </div>

      {pageNumbers.map((p, i) =>
        p === "…" ? (
          <span key={`ellipsis-${i}`} className="w-5 text-center text-sm text-steel-400">
            …
          </span>
        ) : (
          <button
            key={p}
            type="button"
            aria-label={`${t("catalogPageAriaPrefix")} ${p}`}
            aria-current={p === page ? "page" : undefined}
            onClick={() => onChange(p)}
            className={`flex h-10 w-10 shrink-0 items-center justify-center text-sm font-semibold transition-colors duration-200 ease-engineered ${
              p === page
                ? "bg-accent text-white"
                : "text-steel-600 hover:bg-steel-100 hover:text-ink"
            }`}
          >
            {p}
          </button>
        )
      )}

      <div className="flex items-center gap-0.5">
        <button
          type="button"
          aria-label={t("catalogNextPageAria")}
          disabled={page >= pageCount}
          onClick={() => onChange(page + 1)}
          className={navButtonClass}
        >
          <ChevronRight size={22} />
        </button>
        {showJump && (
          <button
            type="button"
            aria-label={t("catalogLastPageAria")}
            disabled={page >= pageCount}
            onClick={() => onChange(pageCount)}
            className={navButtonClass}
          >
            <ChevronsRight size={18} />
          </button>
        )}
      </div>
    </div>
  );
}

export default function CatalogPage() {
  const { t, language } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";

  const [searchInput, setSearchInput] = useState(query);
  // Ghost text (inline autocomplete) + chip row, from the lightweight
  // /api/search/suggestions endpoint — separate from the actual search
  // index/results above, which only recompute on submit. Debounced so a
  // fast typist doesn't fire a request per keystroke.
  const [suggestions, setSuggestions] = useState<{ ghost: string; chips: string[] }>({
    ghost: "",
    chips: [],
  });
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed.length < 3) {
      setSuggestions({ ghost: "", chips: [] });
      return;
    }
    const timeoutId = setTimeout(() => {
      fetch(`/api/search/suggestions?q=${encodeURIComponent(trimmed)}`)
        .then((res) => (res.ok ? res.json() : null))
        .then((data: { ghost: string; chips: string[] } | null) => {
          if (data) setSuggestions(data);
        })
        .catch(() => {
          // Suggestions are a nice-to-have on top of search, not required
          // for it to work — a failed fetch just leaves the row/ghost text
          // empty rather than surfacing an error to the visitor.
        });
    }, 180);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  // Seeded from the URL so returning from a product page (via the "Каталог"
  // back link, which restores this exact URL from sessionStorage) lands back
  // on the same page instead of resetting to 1.
  const [page, setPage] = useState(() => {
    const fromUrl = Number(searchParams.get("page"));
    return fromUrl > 0 ? fromUrl : 1;
  });

  const isSearchActive = query.length > 0;

  // Same drawer mechanics as RFQDrawer (cart panel) — Escape closes it, and
  // body scroll is locked while it's open so the page behind it can't scroll.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsFilterPanelOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isFilterPanelOpen ? "hidden" : "";
  }, [isFilterPanelOpen]);

  // Indexing is the expensive part (1600+ products) — only redo it when the
  // catalog itself changes, not on every keystroke. Search then runs
  // entirely client-side against the already-loaded catalog: instant, and
  // no dependency on an external API for something that used to take 10+
  // seconds per query.
  const searchIndex = useMemo(() => createProductSearchIndex(products), [products]);
  const searchResults = useMemo(
    () => (isSearchActive ? searchProducts(searchIndex, query) : []),
    [searchIndex, isSearchActive, query]
  );
  // Facet filters (category, brand, characteristics) apply on top of a
  // search — narrowing to the query's matches first, then letting the
  // sidebar refine within them, same as any faceted search UI.
  const baseProducts = isSearchActive ? searchResults : products;

  // Seeded once from whatever the URL held on load — either restored from a
  // reload, or carried over from the homepage's "Полный каталог" link.
  const [initialFilters] = useState(() => parseFiltersFromSearchParams(searchParams));
  const filters = useCatalogFilters(baseProducts, initialFilters);

  // Remembers the catalog's current URL (filters and all) so a product
  // page's "Каталог" back link can return to this exact filtered view
  // instead of a blank /catalog — read by ProductDetailPage on mount.
  useEffect(() => {
    sessionStorage.setItem("lastCatalogUrl", `/catalog?${searchParams.toString()}`);
  }, [searchParams]);

  // Keeps the URL in sync with the current filter picks (replacing, not
  // pushing, so it doesn't spam browser history) — this is what lets a
  // reload land back on the same filtered view, and lets the filters
  // survive until the user explicitly clears them via "Очистить всё".
  useEffect(() => {
    const next = filtersToSearchParams({
      activeCategory: filters.activeCategory,
      activeSubcategory: filters.activeSubcategory,
      selectedBrands: filters.selectedBrands,
      inStockOnly: filters.inStockOnly,
      numericFilters: filters.numericFilters,
      selectFilters: filters.selectFilters,
      sort: filters.sortBy,
    });
    if (query) next.set("q", query);
    if (page > 1) next.set("page", String(page));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.activeCategory,
    filters.activeSubcategory,
    filters.selectedBrands,
    filters.inStockOnly,
    filters.numericFilters,
    filters.selectFilters,
    filters.sortBy,
    page,
  ]);

  useEffect(() => {
    fetch("/api/products")
      .then((res) => res.json())
      .then(setProducts)
      .catch(() => {})
      .finally(() => setIsLoadingProducts(false));
  }, []);

  // Jump back to page 1 whenever the result set actually changes underneath
  // the pager — otherwise a filter/search change could leave the user
  // stranded on a page number that no longer has any items. Compares a
  // serialized signature rather than just "did this effect run before",
  // because StrictMode's dev-only double-invoke of mount effects would
  // otherwise reset a page number restored from the URL (see the page
  // useState above) right back to 1 before the user does anything.
  const filterSignature = JSON.stringify({
    query,
    activeCategory: filters.activeCategory,
    activeSubcategory: filters.activeSubcategory,
    selectedBrands: Array.from(filters.selectedBrands).sort(),
    inStockOnly: filters.inStockOnly,
    numericFilters: filters.numericFilters,
    selectFilters: Object.fromEntries(
      (Object.entries(filters.selectFilters) || []).map(([k, v]) => [k, Array.from(v).sort()])
    ),
  });
  const prevFilterSignature = useRef(filterSignature);
  useEffect(() => {
    if (prevFilterSignature.current === filterSignature) return;
    prevFilterSignature.current = filterSignature;
    setPage(1);
  }, [filterSignature]);

  useEffect(() => {
    setSearchInput(query);
  }, [query]);

  // Track the top match (not the raw query text) so recommendations can join
  // it straight to a product/category, same as cart_add does. Fires once per
  // distinct query, not on every incidental re-render.
  useEffect(() => {
    if (isSearchActive && searchResults.length > 0) {
      trackInteraction("search", searchResults[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const submitSearch = (value: string) => {
    const trimmed = value.trim();
    const next = new URLSearchParams(searchParams);
    if (trimmed) next.set("q", trimmed);
    else next.delete("q");
    setSearchParams(next);
    setSuggestions({ ghost: "", chips: [] });
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitSearch(searchInput);
  };

  // A chip is a complete query on its own — clicking one both fills the
  // input and runs the search immediately, matching how a real autocomplete
  // row behaves (the visitor shouldn't have to hit Enter a second time).
  const handleChipClick = (chip: string) => {
    setSearchInput(chip);
    submitSearch(chip);
  };

  const clearSearch = () => {
    setSearchInput("");
    const next = new URLSearchParams(searchParams);
    next.delete("q");
    setSearchParams(next);
    setSuggestions({ ghost: "", chips: [] });
  };

  // Grouping happens after filtering/sorting — a variant matching an active
  // filter is enough to surface its whole group, and pagination then counts
  // series (one card each), not raw SKUs.
  const groupedForDisplay = buildProductGroups(filters.displayed);

  // Default browse view only (no search, no category/brand/characteristic
  // filter, default sort) — everything else (a search, a chosen category,
  // an explicit price/name sort, ...) has its own real order that this
  // must not disturb. Page 1 keeps the curated featured/recommended order
  // from sortProducts untouched; from page 2 on, the rest of the catalog
  // shuffles instead of always showing the same tail in the same sequence
  // — with non-tableware equipment shuffled ahead of tableware, so
  // dishware/glassware only starts showing up once the equipment catalog
  // is exhausted.
  const isDefaultBrowseView =
    !isSearchActive &&
    filters.activeCategory === "All" &&
    filters.activeFilterCount === 0 &&
    filters.sortBy === "relevance";
  // Only the shuffle itself is memoized, not the combined list — keeping
  // the non-default branch outside this memo means it's never at risk of
  // serving a stale list from before a filter changed. Deliberately keyed
  // on `products`/`isDefaultBrowseView`, not on `groupedForDisplay` (a
  // fresh array every render): depending on that would reshuffle on every
  // render, including a plain page click, making items jump between pages.
  const shuffledTail = useMemo(() => {
    if (!isDefaultBrowseView) return null;
    const rest = groupedForDisplay.slice(PAGE_SIZE);
    const equipment = rest.filter((g) => g.cheapest.category !== "Tableware");
    const tableware = rest.filter((g) => g.cheapest.category === "Tableware");
    return [...shuffleArray(equipment), ...shuffleArray(tableware)];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, isDefaultBrowseView]);
  const displayedGroups =
    isDefaultBrowseView && shuffledTail
      ? [...groupedForDisplay.slice(0, PAGE_SIZE), ...shuffledTail]
      : groupedForDisplay;
  const pageCount = Math.max(1, Math.ceil(displayedGroups.length / PAGE_SIZE));
  const pageItems = displayedGroups.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const goToPage = (next: number) => {
    setPage(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // The brand picker's scroll box (in the sidebar) targets the bottom edge of
  // the product grid's last row, so the two columns visually end on the
  // same line instead of the picker trailing off at an arbitrary height.
  const gridWrapRef = useRef<HTMLDivElement>(null);
  const brandAlignY = useGridLastRowBottom(gridWrapRef, [pageItems]);

  // Reflects the active category/subcategory drill-down, same idea as the
  // reference site's "Home > Shop > Macchinari > Trattamento Contenitori".
  const breadcrumbItems: BreadcrumbItem[] = [{ label: t("catalogLabel"), href: "/catalog" }];
  if (filters.activeCategory !== "All") {
    breadcrumbItems.push({
      label: CATEGORY_LABELS[filters.activeCategory],
      href: `/catalog?category=${filters.activeCategory}`,
    });
  }
  if (filters.activeSubcategory) {
    breadcrumbItems.push({ label: filters.activeSubcategory });
  }

  // Regional SEO intro shown under the category H1 — targets city-qualified
  // searches ("пароконвектоматы купить в Алматы") on every major category
  // page, not just the homepage.
  const activeCategoryLabel = filters.activeSubcategory ?? CATEGORY_LABELS[filters.activeCategory];

  useEffect(() => {
    const previousTitle = document.title;
    if (filters.activeCategory !== "All") {
      document.title = `${activeCategoryLabel} для ресторанов и баров купить в Казахстане (Алматы, Астана) — BARCRAFT`;
    }
    return () => {
      document.title = previousTitle;
    };
  }, [filters.activeCategory, activeCategoryLabel]);
  // activeCategoryLabel is undefined on the unfiltered catalog view
  // (activeCategory === "All", no subcategory) — CATEGORY_LABELS has no
  // "All" entry, since that's a sentinel meaning "no filter," not a real
  // category. This intro is only ever rendered when a category IS
  // selected (see the JSX below), but it's still computed unconditionally
  // on every render, so it must degrade to "" rather than call
  // .toLowerCase() on undefined and crash the whole catalog page.
  const regionalIntro = !activeCategoryLabel
    ? ""
    : language === "kk"
      ? `Алматы, Астана және Қазақстанның барлық аймақтарына кәсіби ${activeCategoryLabel} жеткіземіз. Ресми кепілдік, сервистік қызмет көрсету, заңды тұлғалармен жұмыс.`
      : `Поставки профессионального ${activeCategoryLabel.toLowerCase()} в Алматы, Астану и по всему Казахстану. Официальная гарантия, сервис, работа с юридическими лицами.`;

  // Visible "about this brand" blurb — only when the visitor has narrowed
  // the sidebar's brand filter down to exactly one brand, and only for
  // brands with an entry in BRAND_DESCRIPTIONS (most don't, and render
  // nothing). Distinct from each product's own collection description.
  const singleSelectedBrand =
    filters.selectedBrands.size === 1 ? Array.from(filters.selectedBrands)[0] : undefined;
  const brandDescription = singleSelectedBrand ? BRAND_DESCRIPTIONS[singleSelectedBrand] : undefined;

  return (
    <section className="bg-steel-50 pb-24 md:pb-32">
      <CatalogVideoBanner brandName={singleSelectedBrand} brandDescription={brandDescription} />
      <Breadcrumbs items={breadcrumbItems} />

      <div className="container-industrial mt-10">
        {/* Full-width row, not squeezed into the 240px sidebar column below —
            gives the animated placeholder room to breathe and reads as the
            page's primary search action rather than a sidebar afterthought. */}
        <form onSubmit={handleSearchSubmit} className="flex items-center gap-1.5">
          <AnimatedSearchInput
            value={searchInput}
            onChange={setSearchInput}
            onClear={clearSearch}
            showClear={isSearchActive}
            className="flex-1"
            ghostSuggestion={suggestions.ghost}
            onAcceptGhost={setSearchInput}
          />
          <button
            type="button"
            onClick={() => setIsFilterPanelOpen((v) => !v)}
            aria-label={t("filtersButton")}
            className="relative flex h-14 w-14 shrink-0 items-center justify-center border border-steel-200 bg-white text-steel-600 hover:border-accent hover:text-ink sm:w-auto sm:gap-2 sm:px-5 lg:hidden"
          >
            <Filter size={22} />
            <span className="hidden text-xs font-semibold uppercase tracking-wide2 sm:inline">
              {t("filtersButton")}
            </span>
            {filters.activeFilterCount > 0 && (
              <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center bg-accent px-1 text-[11px] font-semibold leading-none text-white">
                {filters.activeFilterCount}
              </span>
            )}
          </button>
        </form>

        {/* Fixed-height reserved slot, present whether or not suggestions are
            showing, so the catalog grid below never shifts as chips
            appear/disappear while typing. Sized tight to the chip buttons'
            own content height (py-1.5 + text-sm) rather than padded out -
            this is dead space on every visit where nothing's typed yet. */}
        <div className="mt-2 h-9">
          {/* Always mounted (never conditionally rendered) and animated via
              class toggle rather than mount/unmount, so chips crossfade and
              settle in with a slight rise instead of popping in/out as
              typing debounces resolve. */}
          <div
            // Sharp corners, matching the search input and ФИЛЬТРЫ button
            // right above (neither has a rounded-* class, i.e. rounded-none)
            // — an earlier rounded-pill version read as visually inconsistent
            // with the rest of this page. Scrollbar hidden unconditionally
            // (no-scrollbar, not the mobile-only variant) so the row scrolls
            // as a clean swipeable strip on every viewport.
            className={`no-scrollbar -mx-1 flex h-full flex-nowrap items-center gap-2 overflow-x-auto whitespace-nowrap px-1 transition-all duration-200 ease-engineered ${
              suggestions.chips.length > 0
                ? "translate-y-0 opacity-100"
                : "pointer-events-none -translate-y-1 opacity-0"
            }`}
          >
            {suggestions.chips.map((chip) => (
              <button
                key={chip}
                type="button"
                onClick={() => handleChipClick(chip)}
                className="shrink-0 whitespace-nowrap border border-steel-200 bg-white px-3 py-1.5 text-sm text-ink transition-colors duration-200 ease-engineered hover:border-accent hover:text-accent active:bg-steel-50"
              >
                <span className="font-semibold">{chip.slice(0, searchInput.length)}</span>
                <span className="text-steel-500">{chip.slice(searchInput.length)}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="mt-2 grid grid-cols-1 gap-10 lg:grid-cols-[240px_1fr]">
          {/* hidden (not just the aside inside it) so this whole grid item is
              removed from layout on mobile — a merely-empty-but-still-present
              grid item still claims a full gap-10 row gap even at zero
              height, which was stacking with the fixed-height suggestion
              slot above into a lot of dead space under the search bar. */}
          <div className="hidden lg:flex lg:flex-col lg:gap-6">
            <aside>
              <CatalogSidebar filters={filters} brandPickerAlignBottomY={brandAlignY} />
            </aside>
          </div>

          {/* Mobile filter drawer — same fixed backdrop + translate-x slide
              mechanics as RFQDrawer (the cart panel), just entering from the
              left instead of the right so the two don't read as the same
              action. Desktop keeps the static column above untouched. */}
          <div
            onClick={() => setIsFilterPanelOpen(false)}
            className={`fixed inset-0 z-[60] bg-ink/60 transition-opacity duration-300 ease-engineered lg:hidden ${
              isFilterPanelOpen ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
            }`}
          />
          <aside
            aria-hidden={!isFilterPanelOpen}
            className={`fixed inset-y-0 left-0 z-[70] flex w-full max-w-xs flex-col border-r border-steel-200 bg-white transition-transform duration-300 ease-engineered lg:hidden ${
              isFilterPanelOpen ? "translate-x-0" : "-translate-x-full"
            }`}
          >
            <div className="flex items-center justify-between border-b border-steel-200 px-5 py-4">
              <h2 className="font-sans text-sm font-semibold text-ink">{t("filtersButton")}</h2>
              <button
                type="button"
                aria-label={t("catalogCloseFiltersAria")}
                onClick={() => setIsFilterPanelOpen(false)}
                className="flex h-9 w-9 items-center justify-center text-ink transition-colors duration-150 ease-engineered hover:bg-steel-900/5 hover:text-accent active:bg-steel-900/5"
              >
                <X size={20} />
              </button>
            </div>
            <div className="no-scrollbar flex-1 overflow-y-auto p-5">
              <CatalogSidebar filters={filters} />
            </div>
          </aside>

          <div>
            {isSearchActive && (
              <p className="mb-4 text-sm text-steel-600">
                {t("catalogSearchResultsPrefix")} «{query}»: {searchResults.length}
              </p>
            )}
            {!isSearchActive && filters.activeCategory !== "All" && (
              // Present for search engines (real heading + on-topic intro
              // paragraph), but visually hidden — the toolbar just below
              // already shows the category name, so a second, larger
              // heading here added nothing for a sighted visitor. h2, not
              // h1 — CatalogVideoBanner's heading above is the page's one
              // real H1 on every catalog view, category-filtered or not.
              <div className="sr-only">
                <h2>{activeCategoryLabel}</h2>
                <p>{regionalIntro}</p>
              </div>
            )}
            <CatalogToolbar filters={filters} />
            {isLoadingProducts ? (
              <ProductGridSkeleton count={PAGE_SIZE} />
            ) : (
              <>
                <ProductGrid gridRef={gridWrapRef} groups={pageItems} columns="2-3-4" />
                {filters.displayed.length === 0 && (
                  <p className="mt-6 text-sm text-steel-500">
                    {isSearchActive ? t("catalogEmptySearch") : t("catalogEmptyFilters")}
                  </p>
                )}
                <PaginationControls page={page} pageCount={pageCount} onChange={goToPage} />
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
